import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GetIntegrationAppDetailsResponse, GetPbxIntegrationAppInstallationsRequest, InstallPbxIntegrationAppRequest, InstallPbxIntegrationAppRequest_ApiKey, IntegrationAppInstallation_State } from '@proto/odoo/integrations';
import { exhaustMap, finalize, firstValueFrom, takeWhile, timer } from 'rxjs';
import { IntegrationAppsService } from '../../integration-apps.service';
import { ApiPermissions, IntegrationApp } from '@proto/integrationapp';
import { Subscription } from 'src/app/subscription/subscription.model';
import { SubscriptionService } from 'src/app/subscription/subscription.service';
import { ToastService } from 'src/app/toast.service';
import { I18NextService } from 'angular-i18next';
import { AutorunComponent } from '@myshared/autorun.component';
import { Model } from 'src/app/app.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InstallationDialogContentType, SubWithVersion } from '../../integration-apps.types';
import { UpgradeAppInstallationEvent } from 'src/app/subscription/subscription-card/subscription-card.component';

@Component({
  selector: 'app-integration-apps-details',
  templateUrl: './integration-apps-details.component.html',
  standalone: false
})
export class IntegrationAppsDetailsComponent extends AutorunComponent implements OnInit {

  // vars to show information on the details page
  public integrationAppId: string = '';
  public integrationAppDetailsResponse: GetIntegrationAppDetailsResponse;
  public integrationApp: IntegrationApp;
  public integrationAppVersion?: number;
  public integrationAppOwnerId?: number;
  public subscriptions: Subscription[];
  public subscriptionsWithAppInstalledInitial: SubWithVersion[] = [];
  public subscriptionsWithAppInstalledToDisplay: SubWithVersion[] = [];

  // vars to show data in the installation dialog
  public dialogContentType: InstallationDialogContentType = '';
  public showDialog: boolean = false;
  public subscriptionsToShowForInstallation: Subscription[];
  public preselectedSubId?: string;
  public preselectedSubName?: string;
  public preselectedSubscription: Subscription;
  public previousApiPermissions?: ApiPermissions[];
  public isUpgrade: boolean = false;

  public applianceIdToUninstallFrom: string = '';
  public isAdmin: boolean;

  public failedInstallationApplianceIds = new Set<string>();

  private destroyRef = inject(DestroyRef);
  private installationStatusPolling = false;

  constructor(private router: Router,
    private integrationService: IntegrationAppsService,
    private subscriptionService: SubscriptionService,
    private route: ActivatedRoute,
    private toastService: ToastService,
    private i18Next: I18NextService,
    private m: Model
  ) {
    super();
  }

  async ngOnInit() {
    this.route.paramMap.subscribe(r => {
      this.integrationAppId = r.get('appId') ?? '';
    });
    this.route.queryParams.subscribe(params => {
      this.preselectedSubId = params['preselectedSubId'];
      this.preselectedSubName = params['preselectedSubName'];
    });
    this.autorun(() => {
      this.isAdmin = this.m.account.isMypascomAdmin;
    });

    await this.fetchIntegrationAppData();
    const hasPendingInstallation = await this.refreshInstallationStatus();

    if (hasPendingInstallation) {
      this.startInstallationStatusPolling();
    }
  }

  private async refreshInstallationStatus(): Promise<boolean> {
    if (!this.integrationApp || !this.subscriptions) {
      return false;
    }
    try {
      const request = GetPbxIntegrationAppInstallationsRequest.create({
        appUuid: this.integrationApp.uuid,
      });

      const response = await firstValueFrom(
        this.integrationService.getAppInstallations(request)
      );

      const installedSubscriptions: SubWithVersion[] = [];
      const idsOfAppliancesWithFailedInstallations = new Set<string>();
      let hasPendingInstallation = false;

      for (const installation of response.installations ?? []) {
        const state = installation.installation?.state;

        if (state === IntegrationAppInstallation_State.STATE_INSTALL_FAILED) {
          idsOfAppliancesWithFailedInstallations.add(String(installation.applianceId));
        }

        if (this.integrationService.isPendingState(state)) {
          hasPendingInstallation = true;
        }

        const applianceId = String(installation.applianceId);
        const subscription = this.subscriptions.find(
          sub => String(sub.appliance_id) === applianceId
        );

        if (subscription) {
          const subWithVersion: SubWithVersion = {
            sub: subscription,
            version: installation.installation?.installedVersion,
            state: installation.installation?.state,
          }
          installedSubscriptions.push(subWithVersion);
        }
      }
      this.failedInstallationApplianceIds = idsOfAppliancesWithFailedInstallations;
      this.subscriptionsWithAppInstalledInitial = installedSubscriptions;
      this.subscriptionsWithAppInstalledToDisplay = installedSubscriptions;

      return hasPendingInstallation;
    } catch (error) {
      this.toastService.error(this.i18Next.t('integration_apps_fetch_installation_status_error'), 'error');
      return false;
    }
  }

  async fetchIntegrationAppData() {
    try {
      this.integrationAppDetailsResponse = await firstValueFrom(this.integrationService.getAppDetails(this.integrationAppId));
      this.subscriptions = await firstValueFrom(this.subscriptionService.getSubscriptions());
    } catch (error) {
      this.toastService.error(this.i18Next.t('integration_apps_load_resources_error'), 'error');
      return;
    }

    if (this.integrationAppDetailsResponse.app) {
      this.integrationApp = this.integrationAppDetailsResponse.app;
      this.integrationAppVersion = this.integrationAppDetailsResponse.appVersion;
      this.integrationAppOwnerId = this.integrationAppDetailsResponse.appOwner?.ownerId;
    } else {
      this.toastService.error(this.i18Next.t('integration_apps_app_details_error'), 'error');
      return;
    }
  }

  filter(event: string) {
    const search = event.toLowerCase();
    this.subscriptionsWithAppInstalledToDisplay = this.subscriptionsWithAppInstalledInitial.filter(({ sub }) =>
      sub && (sub.name?.toLowerCase().includes(search) || sub.customer_name?.toLowerCase().includes(search))
    );
  }

  onBack() {
    this.router.navigate(['/integration-apps/hub']);
  }

  async startAppInstallationUpgrade(event: UpgradeAppInstallationEvent) {

    const preselectedSub = this.subscriptions.find(
      sub => sub.appliance_id === event.applianceId
    );
    if (!preselectedSub) {
      return;
    } else {
      this.preselectedSubscription = preselectedSub;
    }

    const previousAppVersion = await firstValueFrom(this.integrationService.getAppDetails(this.integrationAppId, event.installedVersion));
    this.previousApiPermissions = previousAppVersion.app?.apiPermissions;

    this.failedInstallationApplianceIds.clear();
    this.isUpgrade = true;
    this.dialogContentType = 'showResources';
    this.showDialog = true;
  }

  async startInstallationProcess(event: { appId: string }) {
    this.isUpgrade = false;
    this.previousApiPermissions = undefined;
    try {
      const request = GetPbxIntegrationAppInstallationsRequest.create({
        appUuid: event.appId,
      });
      const response = await firstValueFrom(this.integrationService.getAppInstallations(request));

      this.failedInstallationApplianceIds = new Set(
        (response.installations ?? []).filter(installation =>
          installation.installation?.state === IntegrationAppInstallation_State.STATE_INSTALL_FAILED
        ).map(installation => String(installation.applianceId))
      );

      const installationsByAppliance = new Map<string, IntegrationAppInstallation_State | undefined>();
      for (const installation of response.installations ?? []) {
        installationsByAppliance.set(
          String(installation.applianceId),
          installation.installation?.state
        );
      }

      if (this.preselectedSubId) {
        const preselectedSub = this.subscriptions.find(
          sub => String(sub.appliance_id) === String(this.preselectedSubId)
        );
        if (!preselectedSub) {
          return;
        }

        const applianceId = String(preselectedSub.appliance_id);
        const installationState = installationsByAppliance.get(applianceId);

        if (installationsByAppliance.has(applianceId) && !this.isInstallableState(installationState)) {
          return;
        }
        this.preselectedSubscription = preselectedSub;
      } else {
        this.subscriptionsToShowForInstallation = this.subscriptions.filter(sub => {
          const applianceId = String(sub.appliance_id);

          // no installation exists for this appliance
          if (!installationsByAppliance.has(applianceId)) {
            return true;
          }
          // checks state of installation
          return this.isInstallableState(installationsByAppliance.get(applianceId));
        });
      }
    } catch (error) {
      this.toastService.error(this.i18Next.t('integration_apps_load_resources_error'), 'error');
      return;
    }
    this.dialogContentType = this.preselectedSubId ? 'showResources' : 'selectSubscription';
    this.showDialog = true;
  }

  async retryInstallation(applianceId: number ) {
    let apiKeySets: InstallPbxIntegrationAppRequest_ApiKey[] = [];
    for (let permissionSet of this.integrationApp.apiPermissions) {
      const apiKey = InstallPbxIntegrationAppRequest_ApiKey.create({
        identifier: permissionSet.identifier
      });
      apiKeySets.push(apiKey);
    }
    
    const req = InstallPbxIntegrationAppRequest.create({
      applianceId: applianceId,
      integrationAppUuid: this.integrationAppId,
      integrationAppVersion: this.integrationAppVersion,
      apiKeys: apiKeySets
    });

    try {
      await firstValueFrom(this.integrationService.installApp(req));

      const hasPendingInstallation = await this.refreshInstallationStatus();

      if (hasPendingInstallation) {
        this.startInstallationStatusPolling();
      }
    } catch (error) {
      this.toastService.error(this.i18Next.t('integration_apps_retry_installation_error'), 'error');
      return;
    }
  }

  private isInstallableState(state?: IntegrationAppInstallation_State): boolean {
    return state === IntegrationAppInstallation_State.STATE_UNINSTALLED;
  }

  async installationSuccess() {
    await this.fetchIntegrationAppData();
    const hasPendingInstallation = await this.refreshInstallationStatus();
    if (hasPendingInstallation) {
      this.startInstallationStatusPolling();
    }
  }

  confirmUninstallation(applianceId: string) {
    this.applianceIdToUninstallFrom = applianceId;
    this.dialogContentType = 'confirmUninstallation';
    this.showDialog = true;
  }

  hideTheDialog() {
    this.showDialog = false;
  }

  async startUninstallation() {
    const success = await this.integrationService.uninstallAppWithFeedback(
      parseInt(this.applianceIdToUninstallFrom),
      this.integrationApp.uuid
    );
    if (success) {
      this.hideTheDialog();
      await this.fetchIntegrationAppData();

      const hasPendingInstallation = await this.refreshInstallationStatus();
      if (hasPendingInstallation) {
        this.startInstallationStatusPolling();
      }
    }
  }

  private startInstallationStatusPolling() {
    if (this.installationStatusPolling) {
      return;
    }
    this.installationStatusPolling = true;
    timer(20_000, 20_000)
      .pipe(
        exhaustMap(() => this.refreshInstallationStatus()),
        takeWhile(hasPending => hasPending, true),
        finalize(() => {
          this.installationStatusPolling = false;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
  
}
